import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Alert, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { API } from "../utils/API";

function OrdersFileUploadModal(props) {
    const [form] = Form.useForm();
    const [errorMessage, setErrorMessage] = useState("");
    const [file, setFile] = useState(null);

    useEffect(() => {
        setErrorMessage("");
        form.resetFields();
    }, [props.ordersFileUploadVisible, form]);

    const handleFileChange = (info) => {
        setErrorMessage("");
        const allowedExt = /(\.csv)$/i;
        // Update file state when a valid file is selected
        if (info.fileList.length > 0 && info.fileList[0].type === "text/csv" && allowedExt.exec(info.fileList[0].name)) {
            setFile(info.fileList[0].originFileObj);
        } else {
            setFile(null); // Clear the file if removed
        }
    };

    const handleUpload = async () => {
        if (!file) {
            message.error("Please select a CSV file to upload.");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            const fileContent = event.target.result;

            // Parsing CSV content manually, will consider using a library (e.g. papaparse) if the CSV becomes large or complex for parsing.
            const rows = fileContent
                .split("\n")
                .map(line => line.trim())
                .filter(line => line.length > 0) // Ignore blank or whitespace only lines
                .map((line) => line.split(",").map((cell) => cell.trim()));

            if (rows.length < 2) { // The header and at least one data row
                setErrorMessage(`CSV is empty.`);
                return;
            }

            // Validate each row (skip header)
            const invalidRows = rows.slice(1).filter((row) => {
                if (row.length !== 3) return true;
                const [product, access_code, order_number] = row;
                return (!product || !access_code || !order_number);
            });

            if (invalidRows.length > 0) {
                setErrorMessage(`CSV contains invalid rows: ${invalidRows.length}`);
                return;
            }

            const formData = new FormData();
            formData.append("file", file);

            try {
                API.uploadOrders(formData).then((res) => {
                    if (res.ok) {
                        message.success("New orders saved to database successfully!");
                        props.setOrdersFileUploadVisible(false) // Close modal on success
                        API.getOrders().then((res) => props.setOrders(res.data))
                    } else {
                        const errMsg = res.data.message.toLowerCase();
                        if (errMsg.includes("duplicate")) {
                            setErrorMessage("Please make sure product serial numbers are unique.");
                        } else if (errMsg.includes("validation failed")) {
                            setErrorMessage("CSV contains invalid rows.");
                        } else if (errMsg.includes("invalid file type")) {
                            setErrorMessage("Invalid file type.");
                        } else {
                            setErrorMessage("Error uploading file");
                        }
                    }
                });
            } catch (error) {
                console.error("Error uploading file:", error);
            }
        };

        reader.readAsText(file);
    };

    const handleCancel = () => {
        props.setOrdersFileUploadVisible(false);
        setFile(null); // Clear the selected file on cancel
    };

    return (
        <Modal
            title="Upload Orders File"
            open={props.ordersFileUploadVisible}
            width={800}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button key="upload" type="primary" onClick={handleUpload}>
                    Upload
                </Button>,
            ]}
        >
            <Form
                form={form}
                labelCol={{
                    span: 2,
                }}
                wrapperCol={{
                    span: 20,
                }}
            >
                <div style={{ marginTop: 16, marginBottom: 24 }}>
                    <p>
                        Please upload a CSV file containing the three fields: Product, Product Serial Number, Order Number.<br />
                        - Fields are comma separated. The first line is the header.<br />
                        - The "Product" field must match exactly the product name already created in the system.
                    </p>
                    Here is a sample file format:
                    <div style={{ fontSize: 12, margin: 10, border: "1px solid #91caff" }}>
                        Product,Product Serial Number,Order Number<br />
                        PDF - 4/Axiom 2025,AXML999999-9999,ST-99-9999<br />
                        PDF - 4/Axiom 2025,AXML999999-0000,ST-99-9999<br />
                        ...
                    </div>
                </div>
                <Form.Item
                    name="fileName"
                    label="File"
                    valuePropName="File"
                    rules={[
                        {
                            required: true,
                            message: "'File' is required",
                        },
                    ]}
                >
                    <Upload
                        onChange={handleFileChange}
                        beforeUpload={() => false} // Prevent auto-upload
                        maxCount={1}
                        listType="picture"
                        accept=".csv"
                        multiple={false}
                    >
                        <Button icon={<UploadOutlined />}>Upload CSV</Button>
                    </Upload>
                </Form.Item>
                {errorMessage.length > 0 && (
                    <Alert
                        style={{ marginBottom: 20 }}
                        message={errorMessage}
                        type="error"
                        closable
                        onClose={() => setErrorMessage("")}
                    />
                )}
            </Form>
        </Modal>
    );
}

export default OrdersFileUploadModal;
